<script context="module" lang="ts">
  const sameWidth: Modifier<any, any> = {
    name: "sameWidth",
    enabled: true,
    phase: "beforeWrite",
    requires: ["computeStyles"],
    fn: ({ state }) => {
      state.styles.popper.minWidth = `${state.rects.reference.width}px`;
    },
    effect: ({ state }) => {
      const width = (state.elements.reference as HTMLElement).offsetWidth;
      state.elements.popper.style.minWidth = `${width}px`;
    },
  };

  const offset: Partial<Modifier<any, any>> = {
    name: "offset",
    // enabled: true,
    // phase: "main",
    // fn: undefined,
    options: {
      offset: ({ placement, reference, popper }) => {
        if (placement === "bottom-start") {
          return [0, -reference.height];
        } else {
          return [];
        }
      },
    },
  };

  declare type BasePlacement = "top" | "bottom" | "right" | "left";
  declare type VariationPlacement = "top-start" | "top-end" | "bottom-start" | "bottom-end" | "right-start" | "right-end" | "left-start" | "left-end";
  declare type AutoPlacement = "auto" | "auto-start" | "auto-end";
  declare type Placement = AutoPlacement | BasePlacement | VariationPlacement;
</script>

<script lang="ts">
  import { createPopper, preventOverflow, flip } from "@popperjs/core";
  import type { Instance, Modifier } from "@popperjs/core";
  import { createEventDispatcher } from "svelte";
  import { appendToBody } from "./utils";

  const dispatch = createEventDispatcher();

  export let open: boolean;
  export let input: HTMLElement;
  export let disableAutoClose: boolean = false;
  export let coverInput: boolean = false;
  export let placement: Placement = "top";

  function close(autoClose?: boolean) {
    if (autoClose && disableAutoClose) return;
    if (open) {
      dispatch("close");
      open = false;
    }
  }

  function handleBodyClick(e: MouseEvent) {
    if (open) {
      const node = e.target as Node;
      if (isMounted(node) && !input.contains(node)) {
        close(true);
      }
    }

    function isMounted(node: Node) {
      if (node.nodeType === Node.DOCUMENT_NODE) return true;
      if (node.parentNode == undefined) return false;
      return isMounted(node.parentNode);
    }
  }

  function popper(node: HTMLElement, options: { input: HTMLElement; open: boolean }) {
    // popper references: process.env...
    if (!globalThis.process) Object.assign(globalThis, { process: { env: {} } });

    let popper: Instance;
    init(input);

    return {
      update(args: { input: HTMLElement; open: boolean }) {
        if (args.input !== options.input) {
          options.input = args.input;
          init(input);
        }
        if (args.open !== options.open) {
          popper?.update();
        }
      },
      destroy() {
        popper?.destroy();
      },
    };

    function init(input: HTMLElement) {
      popper?.destroy();
      if (!input) return;

      if (coverInput) {
        popper = createPopper(input, node, {
          placement: "bottom-start",
          modifiers: [preventOverflow, flip, sameWidth, offset],
        });
      } else {
        popper = createPopper(input, node, {
          placement: placement,
          modifiers: [preventOverflow, flip],
        });
      }
    }
  }

  const overlay = {
    close,
    update() {},
  };
</script>

<svelte:body on:click={handleBodyClick} />

<div use:popper={{ input, open }} use:appendToBody class="overlay-zindex" on:click|stopPropagation>
  {#if open}
    <slot {overlay} />
  {/if}
</div>

<style lang="scss">
  .overlay-zindex {
    z-index: 10000000;
    pointer-events: none;
  }
  :global(.overlay-zindex > *) {
    pointer-events: initial;
  }
</style>
